import clsx from 'clsx';
import { CSSProperties, MouseEvent, ReactElement, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronUp, X } from 'react-feather';

import { noopFunction } from '@vestico/utils';

import { WidgetItemAspectRatio, valueForAspecRatio } from '@vestico/model';

import { getIsRTL } from '@vestico/i18n';

import { useElementSize } from '../hooks';
import { Button } from './Button';
import { DragMove, Movement } from './DragMove';
import style from './Reveal.module.css';

const CLOSE_BUTTON_COLOR = '#424242';
const BUTTON_SIZE = 34;
const BUTTON_STROKE_WIDTH = 3;

export interface RevealProps {
  main: ReactElement | ReactElement[];
  reveal: ReactElement | ReactElement[];
  revealID: string;
  maximumExpandedOffset?: number;
  onTopHeightChange?: (topHeight: number) => void;
  onBottomDragStart?: () => void;
  onBottomDragEnd?: (collapsed: boolean) => void;
  revealDragEnabled: boolean;
  aspectRatio: WidgetItemAspectRatio;
  keepAspectRatioConstraint: boolean;
}

const MIN_TOP_HEIGHT = 180;
const MIN_BOTTOM_HEIGHT = 90;
const ADDITIONAL_DRAG_PADDING = 8;

export const Reveal = ({
  main,
  reveal,
  maximumExpandedOffset,
  onTopHeightChange,
  onBottomDragStart,
  onBottomDragEnd,
  revealDragEnabled,
  revealID,
  aspectRatio,
  keepAspectRatioConstraint,
}: RevealProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const rootSize = useElementSize(rootRef);

  const getIdealTopHeight = useCallback(() => {
    // Ideal top height is 9:16
    return rootSize.width / valueForAspecRatio(aspectRatio);
  }, [rootSize, aspectRatio]);

  const [topHeight, setTopHeight] = useState<number>(getIdealTopHeight());
  const [bottomContainerHeight, setBottomContainerHeight] = useState<number | undefined>(undefined);
  const [bottomExpandedTopOffset, setBottomExpandedTopOffset] = useState(maximumExpandedOffset);
  const [bottomHeight, setBottomHeight] = useState<number | undefined>(undefined);

  const [collapsed, setCollapsed] = useState(true);

  const canExpand = useMemo(() => topHeight !== bottomExpandedTopOffset, [topHeight, bottomExpandedTopOffset]);

  const toggleCollapsed = useCallback(
    (e?: MouseEvent<HTMLElement>) => {
      if (e) {
        e.preventDefault();
      }

      const newCollapsed = !canExpand || !collapsed;
      setCollapsed(newCollapsed);
      if (onBottomDragEnd) {
        onBottomDragEnd(newCollapsed);
      }
    },
    [collapsed, onBottomDragEnd, canExpand]
  );

  const updateContentHeights = useCallback(
    (force?: boolean) => {
      const rootElement = rootRef.current;
      const bottomElement = bottomRef.current;
      if (!rootElement || !bottomElement) {
        console.debug(`No elements available.`);
        return;
      }

      // Don't update when expanded
      if (!collapsed && !force) {
        return;
      }

      // Clone the current bottom element into the DOM and calculate its height separately so we don't interfere with any exinsting height settings
      const botttomClone = bottomElement.cloneNode(true) as HTMLElement;
      botttomClone.style.height = 'auto';
      botttomClone.style.visibility = 'hidden';
      rootElement.appendChild(botttomClone);
      const bottomContentHeight = botttomClone.offsetHeight;
      botttomClone.remove();

      setBottomContainerHeight(bottomContentHeight);
    },
    [collapsed]
  );

  const updateRevealHeights = useCallback(() => {
    const rootHeight = rootSize.height;
    if (!bottomContainerHeight || !rootHeight) {
      console.debug(`No bottom element available.`);
      return;
    }

    const idealTopHeight = getIdealTopHeight();

    const maxHeight = rootHeight - (maximumExpandedOffset ?? 0);
    const intermediateTopHeight = keepAspectRatioConstraint ? Math.min(idealTopHeight, rootHeight - MIN_BOTTOM_HEIGHT) : rootHeight;
    const newTopHeight = Math.max(intermediateTopHeight, MIN_TOP_HEIGHT);
    const finalBottomContainerHeight = Math.max(Math.min(maxHeight, bottomContainerHeight), rootHeight - newTopHeight);

    console.debug(
      `root: ${rootHeight}, bottom: ${bottomContainerHeight}, max: ${maxHeight}, finalBottom: ${finalBottomContainerHeight}, top: ${newTopHeight}`
    );

    setBottomHeight(finalBottomContainerHeight);
    setBottomExpandedTopOffset(rootHeight - finalBottomContainerHeight);
    setTopHeight(newTopHeight);
  }, [bottomContainerHeight, getIdealTopHeight, keepAspectRatioConstraint, maximumExpandedOffset, rootSize.height]);

  useLayoutEffect(() => {
    updateRevealHeights();
  }, [bottomContainerHeight, updateRevealHeights]);

  const topHeightForTopContainer = useMemo(() => (topHeight <= 0 ? 0 : topHeight + ADDITIONAL_DRAG_PADDING), [topHeight]);

  useLayoutEffect(() => {
    if (onTopHeightChange && topHeightForTopContainer > 0) {
      onTopHeightChange(topHeightForTopContainer);
    }
  }, [topHeightForTopContainer, onTopHeightChange]);

  useLayoutEffect(() => {
    updateContentHeights();
  }, [rootRef, bottomRef, updateContentHeights]);

  useLayoutEffect(() => {
    setCollapsed(true);
    updateContentHeights(true);
  }, [revealID]);

  useLayoutEffect(() => {
    updateContentHeights(true);
  }, [rootSize, reveal, updateContentHeights]);

  const [dragTranslateY, setDragTranslateY] = useState<number>(0);
  const [dragDirectionUp, setDragDirectionUp] = useState<boolean | undefined>(undefined);

  const [dragging, setDragging] = useState(false);
  const handleDragMove = useCallback(
    (movement: Movement) => {
      const lastDragTranslate = dragTranslateY;
      const newDragTranslate = movement.y;
      setDragTranslateY(newDragTranslate);

      setDragDirectionUp(lastDragTranslate > newDragTranslate);
    },
    [dragTranslateY]
  );

  const handlePointerDown = useCallback(() => {
    setDragTranslateY(0);
    setDragging(true);
    if (onBottomDragStart) {
      onBottomDragStart();
    }
  }, [onBottomDragStart]);

  const handlePointerUp = useCallback(() => {
    setDragging(false);
    setDragTranslateY(0);

    const newCollapsed = !canExpand || (dragDirectionUp !== undefined ? !dragDirectionUp : collapsed);
    setCollapsed(newCollapsed);
    if (onBottomDragEnd) {
      onBottomDragEnd(newCollapsed);
    }

    setDragDirectionUp(undefined);
  }, [onBottomDragEnd, dragDirectionUp, collapsed, canExpand]);

  const handlePointerCancel = useCallback(() => {
    setDragging(false);
    setDragTranslateY(0);
    if (onBottomDragEnd) {
      onBottomDragEnd(collapsed);
    }
    setDragDirectionUp(undefined);
  }, [onBottomDragEnd, collapsed]);

  const bottomStyle = useMemo(() => {
    const newBottomStyle: CSSProperties = collapsed
      ? { transform: `translateY(${topHeight}px)` }
      : { transform: `translateY(${bottomExpandedTopOffset}px)` };
    if (dragging && bottomExpandedTopOffset && bottomHeight) {
      const minTranslateValue = bottomExpandedTopOffset - ADDITIONAL_DRAG_PADDING;
      const maxTranslateValue = topHeight + ADDITIONAL_DRAG_PADDING;
      const newTranslateValue = collapsed ? topHeight + dragTranslateY : bottomExpandedTopOffset + dragTranslateY;
      const transformValue = Math.min(Math.max(minTranslateValue, newTranslateValue), maxTranslateValue);

      newBottomStyle.transform = `translateY(${transformValue}px)`;
    } else if (topHeight === 0) {
      newBottomStyle.transform = `translateY(100vh)`;
    }

    newBottomStyle.height = `${(bottomHeight ?? 0) + ADDITIONAL_DRAG_PADDING}px`;
    return newBottomStyle;
  }, [bottomExpandedTopOffset, bottomHeight, collapsed, dragTranslateY, dragging, topHeight]);

  const [draggingDrawer, setDraggingDrawer] = useState(false);
  const onDrawerDown = useCallback(() => {
    setDraggingDrawer(true);
    handlePointerDown();
  }, [handlePointerDown]);

  const onDrawerUp = useCallback(() => {
    setDraggingDrawer(false);
    handlePointerUp();
  }, [handlePointerUp]);

  return (
    <div
      className={clsx(style.root)}
      ref={rootRef}
      style={
        topHeight > 0
          ? (`--top-height:${topHeightForTopContainer}px;--additional-drag-padding:${ADDITIONAL_DRAG_PADDING}px;` as CSSProperties)
          : undefined
      }
    >
      <div className={clsx(style.top)}>{main}</div>
      <div className={clsx(style.bottom, { [style.dragging]: dragging })} style={bottomStyle} ref={bottomRef}>
        <DragMove
          onDragMove={handleDragMove}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          draggingEnabled={(collapsed || draggingDrawer) && revealDragEnabled}
        >
          <div
            className={clsx(style.dragArea)}
            onPointerDown={revealDragEnabled ? onDrawerDown : undefined}
            onPointerUp={revealDragEnabled ? onDrawerUp : undefined}
          >
            <div className={clsx(style.drawer)} />
            <div className={clsx(style.close, { [style.rtl]: getIsRTL() }, { [style.visible]: !dragging || (dragging && dragTranslateY === 0) })}>
              <Button
                onClick={toggleCollapsed}
                ariaLabel={collapsed ? 'reveal' : 'collapse'}
                content={
                  collapsed ? (
                    <ChevronUp color={CLOSE_BUTTON_COLOR} size={BUTTON_SIZE} strokeWidth={BUTTON_STROKE_WIDTH} />
                  ) : (
                    <X color={CLOSE_BUTTON_COLOR} size={BUTTON_SIZE} strokeWidth={BUTTON_STROKE_WIDTH} />
                  )
                }
                plain
              />
            </div>
            <>{reveal}</>
          </div>
        </DragMove>
      </div>
    </div>
  );
};

Reveal.defaultProps = {
  maximumExpandedOffset: 60,
  onTopHeightChange: noopFunction,
  onBottomDragStart: noopFunction,
  onBottomDragEnd: noopFunction,
};
